'use client';

import AppImage from '@/components/ui/AppImage';
import { FilterCategory, GalleryImage } from './GalleryInteractive';

const FILTERS: FilterCategory[] = ['All', 'Common Areas', 'Luxos', 'Primera', 'Classica', 'Exterior'];

interface GalleryGridProps {
  images: GalleryImage[];
  activeFilter: FilterCategory;
  onFilterChange: (filter: FilterCategory) => void;
  onImageClick: (index: number) => void;
}

export default function GalleryGrid({ images, activeFilter, onFilterChange, onImageClick }: GalleryGridProps) {
  return (
    <section className="max-w-7xl mx-auto px-4 py-10">
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 justify-center mb-10">
        {FILTERS.map((filter) => (
          <button
            key={filter}
            onClick={() => onFilterChange(filter)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 border ${
              activeFilter === filter
                ? 'bg-primary text-white border-primary shadow-elevated'
                : filter === 'Classica' ?'bg-amber-50 text-amber-700 border-amber-300 hover:border-amber-500 hover:text-amber-800' :'bg-white text-text-secondary border-border hover:border-primary hover:text-primary'
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Masonry Grid */}
      <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
        {images.map((image, index) => (
          <div
            key={image.id}
            onClick={() => onImageClick(index)}
            className="break-inside-avoid cursor-pointer group relative overflow-hidden rounded-xl shadow-subtle hover:shadow-elevated transition-all duration-300"
          >
            <div
              className={`relative w-full overflow-hidden ${
                image.span === 'tall' ?'aspect-[3/4]'
                  : image.span === 'wide' ?'aspect-[4/3]' :'aspect-square'
              }`}
            >
              <AppImage
                src={image.src}
                alt={image.alt}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <span className="inline-block text-xs font-semibold text-white/80 bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">
                      {image.category}
                    </span>
                    {image.tag && (
                      <span className="inline-block text-xs font-semibold text-amber-200 bg-amber-600/70 backdrop-blur-sm px-2 py-0.5 rounded-full">
                        {image.tag}
                      </span>
                    )}
                  </div>
                  <p className="text-white text-sm font-medium leading-snug line-clamp-2">{image.alt}</p>
                </div>
              </div>
              {/* Expand icon */}
              <div className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-subtle">
                <svg className="w-4 h-4 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {images.length === 0 && (
        <div className="text-center py-20 text-text-secondary">
          No photos in this category yet.
        </div>
      )}
    </section>
  );
}
